import React from 'react';
import { Calculator, FileText, ShieldCheck, TrendingUp, ArrowRight, Check } from 'lucide-react';

const DANILO_WHATSAPP_URL =
  'https://wa.me/5516997486389?text=Ol%C3%A1%20estou%20no%20site%20Fernando%20Tech%2C%20Por%20indica%C3%A7%C3%A3o%20deles%20preciso%20de%20consultoria%20Cont%C3%A1bil.';

const FEATURES = [
  { icon: FileText,    text: 'Abertura e Regularização de Empresas' },
  { icon: Calculator,  text: 'Contabilidade Fiscal e Tributária'    },
  { icon: TrendingUp,  text: 'Planejamento Financeiro e Tributário' },
  { icon: ShieldCheck, text: 'Folha de Pagamento e Departamento Pessoal' },
];

const DaniloContabilidadeBanner = () => {
  const handleClick = () => {
    window.open(DANILO_WHATSAPP_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      id="danilo-contabilidade"
      className="py-20 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 relative overflow-hidden"
      aria-labelledby="danilo-contabilidade-title"
    >
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-25 pointer-events-none overflow-hidden">
        <div className="absolute top-10 -left-10 w-96 h-96 bg-blue-700 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-600 rounded-full filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-blue-500/10 border border-blue-400/40 rounded-full">
            <Calculator className="w-4 h-4 text-blue-300" />
            <span className="text-blue-200 font-semibold text-xs uppercase tracking-widest">
              Parceiro Indicado pela Fernando Tech
            </span>
          </div>
          <h2
            id="danilo-contabilidade-title"
            className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight"
          >
            Danilo <span className="bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">Contabilidade</span>
          </h2>
          <p className="text-lg md:text-xl text-blue-100/80 max-w-2xl mx-auto leading-relaxed">
            Consultoria Contábil completa em Ribeirão Preto. Soluções fiscais, abertura de empresa
            e gestão tributária com a confiança de quem é indicado pela Fernando Tech.
          </p>
        </div>

        {/* Conteúdo Principal */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          {/* Lado Esquerdo - Logo + CTA */}
          <div className="relative group">
            {/* Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 rounded-3xl blur-2xl opacity-40 group-hover:opacity-70 transition-opacity duration-500" />

            <button
              type="button"
              onClick={handleClick}
              data-testid="danilo-banner-logo-cta"
              aria-label="Falar com Danilo Contabilidade pelo WhatsApp"
              className="relative w-full bg-white rounded-3xl p-8 md:p-10 shadow-2xl cursor-pointer transition-all duration-500 hover:scale-[1.02] hover:shadow-blue-500/40 flex items-center justify-center"
            >
              <img
                src="./logo-danilo-contabilidade.jpeg"
                alt="Danilo Contabilidade — Escritório de Contabilidade em Ribeirão Preto"
                title="Danilo Contabilidade"
                className="w-full max-w-sm h-auto object-contain"
                loading="lazy"
              />

              {/* Selo flutuante */}
              <span className="absolute top-3 right-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold px-3 py-1.5 rounded-full shadow-lg text-xs flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Indicado
              </span>
            </button>

            <p className="text-center text-blue-200/70 text-sm mt-4 animate-pulse">
              👆 Clique no logo para falar agora pelo WhatsApp
            </p>
          </div>

          {/* Lado Direito - Benefícios + CTA */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight">
                Sua empresa merece uma <span className="text-blue-300">contabilidade de confiança</span>
              </h3>
              <p className="text-slate-300 leading-relaxed">
                Atendimento personalizado, prazos cumpridos e total transparência fiscal —
                exatamente como a Fernando Tech entrega na área de TI.
              </p>
            </div>

            {/* Lista de Serviços */}
            <ul className="space-y-3">
              {FEATURES.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 bg-slate-900/50 backdrop-blur-sm border border-blue-500/20 rounded-xl p-3 hover:border-blue-400/60 hover:bg-slate-900/70 transition-all duration-300"
                >
                  <div className="bg-blue-500/15 border border-blue-400/30 p-2 rounded-lg shrink-0">
                    <f.icon className="w-5 h-5 text-blue-300" />
                  </div>
                  <span className="text-white font-medium text-sm md:text-base">{f.text}</span>
                  <Check className="w-4 h-4 text-emerald-400 ml-auto shrink-0" />
                </li>
              ))}
            </ul>

            {/* CTA principal */}
            <button
              type="button"
              onClick={handleClick}
              data-testid="danilo-banner-cta-button"
              className="group w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-600 via-emerald-500 to-green-500 hover:from-emerald-700 hover:via-emerald-600 hover:to-green-600 text-white font-bold rounded-2xl shadow-2xl shadow-emerald-500/30 transition-all duration-300 hover:scale-[1.02] text-base md:text-lg"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z" />
              </svg>
              Falar com Danilo Contabilidade
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            <p className="text-center text-xs text-slate-400">
              📞 Atendimento via WhatsApp • Resposta rápida • Indicação Fernando Tech
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DaniloContabilidadeBanner;
